import React from 'react';
import { ContextMenu } from '@rocco/ui/context-menu/components/ContextMenu';
import type { ContextMenuItem } from '@rocco/ui/context-menu/components/ContextMenu';
import type { TileBannerMenuAction } from '@rocco/contexts/TileBannerMenuContext';
import { TileAdminTimestampInfo } from '@rocco/ui/tile-shared/components/TileAdminTimestampInfo';
import type { DirectoryItemKPI } from '@rocco/ui/tile-shared/components/TileKPIContextMenu';
import { TileKPIContextMenu } from '@rocco/ui/tile-shared/components/TileKPIContextMenu';
import { AlertDialog } from '@rocco/ui/dialog/components/AlertDialog';

interface AdminContextMenuProps {
    children: React.ReactNode;
    onEdit?: () => void;
    onRepositionImage?: () => void;
    onTogglePin?: () => void;
    onToggleFeature?: () => void;
    /** Pin the article to the article directory's hero carousel. */
    onToggleHeroPin?: () => void;
    onToggleFeaturedBrandPin?: () => void;
    onRemoveFromLibrary?: () => void;
    onDelete?: () => void;
    /** Set the tile's visible carousel slide as the project banner. */
    bannerAction?: TileBannerMenuAction;
    statusInfo?: {
        created?: {
            createdBy: string;
            createdAt: string;
        };
        approved?: {
            approvedBy: string;
            approvedAt: string;
        };
        published?: {
            publishedBy: string;
            publishedAt: string;
        };
        pinned?: boolean;
        featured?: boolean;
        heroPinned?: boolean;
        featuredBrandPinned?: boolean;
        kpi?: DirectoryItemKPI;
    };
}

export const AdminContextMenu: React.FC<AdminContextMenuProps> = ({
    children,
    onEdit,
    onRepositionImage,
    onDelete,
    onTogglePin,
    onToggleFeature,
    onToggleHeroPin,
    onToggleFeaturedBrandPin,
    onRemoveFromLibrary,
    bannerAction,
    statusInfo,
}) => {
    const [openDeleteConfirmation, setOpenDeleteConfirmation] =
        React.useState(false);
    const [
        openRemoveFromLibraryConfirmation,
        setOpenRemoveFromLibraryConfirmation,
    ] = React.useState(false);

    const buildMenuItems = () => {
        const actionItems: ContextMenuItem[] = [];

        if (onEdit) {
            actionItems.push({
                type: 'item',
                render: () => <span>Edit</span>,
                onClick: onEdit,
            });
        }

        if (onRepositionImage) {
            actionItems.push({
                type: 'item',
                render: () => <span>Reposition Image</span>,
                onClick: onRepositionImage,
            });
        }

        // Targets the slide currently visible in the tile's carousel; when
        // that slide already is the banner, show a non-clickable state line.
        if (bannerAction) {
            if (bannerAction.isCurrentBanner) {
                actionItems.push({
                    type: 'label',
                    render: () => <span>Image Is The Project Banner</span>,
                });
            } else {
                actionItems.push({
                    type: 'item',
                    render: () => <span>Set Image As Project Banner</span>,
                    onClick: bannerAction.setCurrentAsBanner,
                });
            }
        }

        if (onTogglePin) {
            actionItems.push({
                type: 'item',
                render: () =>
                    statusInfo?.pinned ? (
                        <span>Unpin From Homepage</span>
                    ) : (
                        <span>Pin To Homepage</span>
                    ),
                onClick: onTogglePin,
            });
        }

        if (onToggleFeature) {
            actionItems.push({
                type: 'item',
                render: () =>
                    statusInfo?.featured ? (
                        <span>Unpin from Top of Directory</span>
                    ) : (
                        <span>Pin to Top of Directory</span>
                    ),
                onClick: onToggleFeature,
            });
        }

        // Names the directory hero it pins to: the pick is global to the
        // article directory, not to the category page the tile sits on.
        if (onToggleHeroPin) {
            actionItems.push({
                type: 'item',
                render: () =>
                    statusInfo?.heroPinned ? (
                        <span>Unpin from Directory Hero</span>
                    ) : (
                        <span>Pin to Directory Hero</span>
                    ),
                onClick: onToggleHeroPin,
            });
        }

        if (onToggleFeaturedBrandPin) {
            actionItems.push({
                type: 'item',
                render: () =>
                    statusInfo?.featuredBrandPinned ? (
                        <span>Unpin From Featured Brands</span>
                    ) : (
                        <span>Pin To Featured Brands</span>
                    ),
                onClick: onToggleFeaturedBrandPin,
            });
        }

        if (onRemoveFromLibrary) {
            actionItems.push({
                type: 'item',
                render: () => <span>Remove From Library</span>,
                onClick: () => setOpenRemoveFromLibraryConfirmation(true),
            });
        }

        if (onDelete) {
            actionItems.push({
                type: 'separator',
            });
            actionItems.push({
                type: 'item',
                render: () => <span>Delete</span>,
                onClick: () => setOpenDeleteConfirmation(true),
            });
        }

        const infoSection: ContextMenuItem[] = [];

        // Both fields blank means nobody was fetched, not that the item has no
        // author — rendering it then leaves a calendar icon and no words.
        const created = statusInfo?.created;
        if (created && (created.createdBy || created.createdAt)) {
            infoSection.push({
                type: 'label',
                render: () => (
                    <TileAdminTimestampInfo
                        action="created"
                        userFullName={created.createdBy}
                        date={created.createdAt}
                    />
                ),
            });
        }

        if (
            statusInfo?.approved?.approvedAt &&
            statusInfo?.approved?.approvedAt
        ) {
            infoSection.push({
                type: 'label',
                render: () => (
                    <TileAdminTimestampInfo
                        action="approved"
                        userFullName={statusInfo?.approved?.approvedBy ?? ''}
                        date={statusInfo?.approved?.approvedAt ?? ''}
                    />
                ),
            });
        }

        if (
            statusInfo?.published?.publishedAt &&
            statusInfo?.published?.publishedBy
        ) {
            infoSection.push({
                type: 'label',
                render: () => (
                    <TileAdminTimestampInfo
                        action="published"
                        userFullName={statusInfo?.published?.publishedBy ?? ''}
                        date={statusInfo?.published?.publishedAt ?? ''}
                    />
                ),
            });
        }

        if (statusInfo?.kpi) {
            infoSection.push({
                type: 'label',
                render: () =>
                    statusInfo.kpi && (
                        <TileKPIContextMenu kpi={statusInfo.kpi} />
                    ),
            });
        }

        if (infoSection.length > 0) {
            actionItems.push({
                type: 'separator',
            });
            actionItems.push(...infoSection);
        }

        return actionItems;
    };

    return (
        <>
            <ContextMenu menuItems={buildMenuItems()}>{children}</ContextMenu>
            {onDelete && (
                <AlertDialog
                    title="Are you absolutely sure?"
                    description="This action cannot be undone. This will permanently delete this product and its associated information and tags."
                    onConfirm={onDelete}
                    controlled={{
                        open: openDeleteConfirmation,
                        setOpen: setOpenDeleteConfirmation,
                    }}
                />
            )}
            {onRemoveFromLibrary && (
                <AlertDialog
                    title="Remove from library?"
                    description="The item will be hidden from the library but kept in the CMS so it can be restored later."
                    onConfirm={onRemoveFromLibrary}
                    controlled={{
                        open: openRemoveFromLibraryConfirmation,
                        setOpen: setOpenRemoveFromLibraryConfirmation,
                    }}
                />
            )}
        </>
    );
};
